import React from 'react';
import {
  Phone,
  MessageSquare,
  Tag,
  FileText,
  CheckSquare,
  Calendar,
  MoreVertical,
  ChevronDown,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getSourceLabel = (source) => {
  const labels = {
    whop: 'Whop',
    manual: 'Manual',
    import: 'Import',
    automation: 'Automation',
    self_repair: 'Self Repair',
    agency: 'Agency',
  };
  return labels[source] || source || 'Unknown';
};

const getSourceColor = (source) => {
  const colors = {
    whop: 'bg-purple-100 text-purple-700',
    manual: 'bg-slate-100 text-slate-700',
    import: 'bg-blue-100 text-blue-700',
    automation: 'bg-amber-100 text-amber-700',
    self_repair: 'bg-emerald-100 text-emerald-700',
    agency: 'bg-indigo-100 text-indigo-700',
  };
  return colors[source] || 'bg-slate-100 text-slate-700';
};

const getStatusColor = (status) => {
  const colors = {
    open: 'bg-blue-100 text-blue-700',
    won: 'bg-emerald-100 text-emerald-700',
    lost: 'bg-red-100 text-red-700',
    review: 'bg-amber-100 text-amber-700',
    archived: 'bg-slate-100 text-slate-600',
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
};

const QuickAction = ({ icon: Icon, tooltip, onClick }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
        >
          <Icon className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const OpportunityListView = ({
  opportunities = [],
  stages = [],
  selectedIds = [],
  onSelectOne,
  onSelectAll,
  onOpenOpportunity,
  onMoveStage,
  loading = false,
}) => {
  // Create a map for quick stage lookup
  const stageMap = {};
  stages.forEach((s) => {
    stageMap[s.id] = s;
  });

  const getStageName = (stageId) => {
    return stageMap[stageId]?.name || stageId;
  };

  const getStageColor = (stageId) => {
    return stageMap[stageId]?.color;
  };

  const allSelected = opportunities.length > 0 && selectedIds.length === opportunities.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < opportunities.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (opportunities.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <div className="text-center">
          <p className="font-medium">No opportunities found</p>
          <p className="text-sm mt-1">Create an opportunity or adjust your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="opportunity-list-view">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-600 uppercase tracking-wider">
        <div className="col-span-1 flex items-center">
          <Checkbox
            checked={allSelected}
            onCheckedChange={() => onSelectAll?.(!allSelected)}
            className={cn(someSelected && 'data-[state=checked]:bg-slate-400')}
          />
        </div>
        <div className="col-span-3 flex items-center gap-1">
          Contact
          <ChevronDown className="h-3 w-3" />
        </div>
        <div className="col-span-2">Stage</div>
        <div className="col-span-1">Source</div>
        <div className="col-span-1 text-right">Value</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-2">Actions</div>
        <div className="col-span-1"></div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-slate-100">
        {opportunities.map((opp) => {
          const contactName = opp.contact_name || opp.title || 'Unknown';
          const isSelected = selectedIds.includes(opp.id);
          const stageColor = getStageColor(opp.stage_id);

          return (
            <div
              key={opp.id}
              className={cn(
                'grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 transition-colors cursor-pointer',
                isSelected && 'bg-emerald-50'
              )}
              onClick={() => onOpenOpportunity?.(opp)}
              data-testid={`list-row-${opp.id}`}
            >
              {/* Checkbox */}
              <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onSelectOne?.(opp.id)}
                />
              </div>

              {/* Contact */}
              <div className="col-span-3 flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                  {getInitials(contactName)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-stone-900 text-sm truncate">{contactName}</p>
                  <p className="text-xs text-slate-500 truncate">{opp.title}</p>
                </div>
              </div>

              {/* Stage */}
              <div className="col-span-2">
                <div className="flex items-center gap-2">
                  {stageColor && (
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stageColor }}
                    />
                  )}
                  <span className="text-sm text-stone-700 truncate">
                    {getStageName(opp.stage_id)}
                  </span>
                </div>
              </div>

              {/* Source */}
              <div className="col-span-1">
                <span className={cn('px-2 py-0.5 rounded text-xs font-medium', getSourceColor(opp.source))}>
                  {getSourceLabel(opp.source)}
                </span>
              </div>

              {/* Value */}
              <div className="col-span-1 text-right">
                <span className="font-semibold text-sm text-stone-900">
                  {formatCurrency(opp.value)}
                </span>
              </div>

              {/* Status */}
              <div className="col-span-1">
                <span className={cn('px-2 py-0.5 rounded text-xs font-medium capitalize', getStatusColor(opp.status))}>
                  {opp.status}
                </span>
              </div>

              {/* Quick Actions */}
              <div className="col-span-2 flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                <QuickAction icon={Phone} tooltip="Call" />
                <QuickAction icon={MessageSquare} tooltip="Message" />
                <QuickAction icon={Tag} tooltip="Tags" />
                <QuickAction icon={FileText} tooltip="Notes" />
                <QuickAction icon={CheckSquare} tooltip="Task" />
                <QuickAction icon={Calendar} tooltip="Appointment" />
              </div>

              {/* More */}
              <div className="col-span-1 flex justify-end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onOpenOpportunity?.(opp)}>
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem>Edit</DropdownMenuItem>
                    <DropdownMenuItem>
                      Move Stage
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OpportunityListView;
